import React, { useMemo } from "react";
import Button from "components/button";
import Icon from "components/icon";
import Typography from "components/typography";
import { Table, Columns } from "components/table";
import Pagination from "components/table/pagination";
import { useNavigate } from "react-router-dom";
import useVendorDetail, { FlattenedAddress, FleetRow } from "./useVendorDetail";
import { TransportResourceVendorBranchDetail } from "_services/modules/transport-resource/vendor";
import VendorDetailCard from "./components/vendor-detail-card";
import VendorHistorySidebar from "./components/vendor-history-sidebar";
import { addressCategoryOptions, getOptionLabel } from "data/address-category";

export const detailVendorPageRouteName = (id = ":id") =>
    `/master-data/vendor/${id}/detail`;

const VendorDetailPage = () => {
    const navigate = useNavigate();

    const {
        id,
        detail,
        flattenedAddresses,
        fleetRows,
        fleetTotalPages,
        isHistoryOpen,
        setIsHistoryOpen,
        activeTab,
        setActiveTab,
        
        // Fleet pagination
        currentPage,
        setCurrentPage,
        rowsPerPage,
        setRowsPerPage,
        sortState,
        setSortState,
        
        // Branch pagination
        branchPage,
        setBranchPage,
        branchRowsPerPage,
        setBranchRowsPerPage,
        totalBranchPages,
        paginatedBranches,
        
        // Address pagination
        addressPage,
        setAddressPage,
        addressRowsPerPage,
        setAddressRowsPerPage,
        totalAddressPages,
        paginatedAddresses,
        
        isLoading,
        isFleetLoading,
        t,
    } = useVendorDetail();
    const getTranslation = (
    key: string,
    fallback: string,
    options?: Record<string, unknown>,
) => {
    // @ts-expect-error temporary i18n key typing limitation
    const val = t(key, options);
        if (val === key) {
            if (options && typeof options === "object") {
                let resolvedFallback = fallback;
                Object.entries(options).forEach(([k, v]) => {
                    resolvedFallback = resolvedFallback.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v));
                });
                return resolvedFallback;
            }
            return fallback;
        }
        return val;
    };

    const renderFleetStatus = (status: string) => {
        const statusLower = status?.toLowerCase() || "";
        let colorClass = "bg-greyScale-90 text-greyScale-60";
        if (statusLower === "available" || statusLower === "active") {
            colorClass = "bg-primary-50 text-primary-600";
        } else if (statusLower === "assigned") {
            colorClass = "bg-yellow-50 text-yellow-600";
        } else if (statusLower === "maintenance") {
            colorClass = "bg-red-50 text-red-600 font-semibold";
        }
        return (
            <div className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${colorClass}`}>
                {status}
            </div>
        );
    };

    const fleetColumns: Columns<FleetRow>[] = useMemo(
        () => [
            {
                id: "no",
                fieldId: "index",
                label: getTranslation("col-no", "No."),
                dragDisabled: true,
                width: "50px",
            },
            {
                id: "plate-number",
                fieldId: "plateNumber",
                label: getTranslation("col-plate-number", "Plate Number"),
                dragDisabled: true,
                sortable: true,
            },
            {
                id: "vehicle-type",
                fieldId: "vehicleType",
                label: getTranslation("col-vehicle-type", "Vehicle Type"),
                dragDisabled: true,
            },
            {
                id: "vehicle-model",
                fieldId: "vehicleModel",
                label: getTranslation("col-vehicle-model", "Vehicle Model"),
                dragDisabled: true,
            },
            {
                id: "odometer",
                fieldId: "odometer",
                label: getTranslation("col-odometer", "Odometer"),
                dragDisabled: true,
            },
            {
                id: "status",
                fieldId: "status",
                label: getTranslation("col-status", "Status"),
                render: (row) => renderFleetStatus(row.status),
                dragDisabled: true,
            },
            {
                id: "stnk-expiry",
                fieldId: "stnkExpiry",
                label: getTranslation("col-stnk-expiry", "STNK Expiry"),
                dragDisabled: true,
            },
            {
                id: "kir-expiry",
                fieldId: "kirExpiry",
                label: getTranslation("col-kir-expiry", "KIR Expiry"),
                dragDisabled: true,
            },
            {
                id: "last-service",
                fieldId: "lastMaintenance",
                label: getTranslation("col-last-service", "Last Service"),
                dragDisabled: true,
            },
            {
                id: "created-by",
                fieldId: "createdBy",
                label: getTranslation("col-created-by", "Created by"),
                render: (row) => (
                    <div className="flex flex-col">
                        <span className="font-semibold text-text-primary">{row.createdBy}</span>
                        <span className="text-xs text-greyScale-60">{row.createdAt}</span>
                    </div>
                ),
                dragDisabled: true,
            },
            {
                id: "updated-by",
                fieldId: "updatedBy",
                label: getTranslation("col-updated-by", "Updated by"),
                render: (row) => (
                    <div className="flex flex-col">
                        <span className="font-semibold text-text-primary">{row.updatedBy}</span>
                        <span className="text-xs text-greyScale-60">{row.updatedAt}</span>
                    </div>
                ),
                dragDisabled: true,
            },
        ],
        [t]
    );

    const branchColumns: Columns<TransportResourceVendorBranchDetail>[] = useMemo(
        () => [
            {
                id: "no",
                fieldId: "index",
                label: getTranslation("col-no", "No."),
                dragDisabled: true,
                width: "50px",
            },
            {
                id: "branch-name",
                fieldId: "branch_name",
                label: getTranslation("col-branch-name", "Branch Name"),
                dragDisabled: true,
            },
            {
                id: "pic-name",
                fieldId: "pic_name",
                label: getTranslation("col-pic-name", "PIC Name"),
                dragDisabled: true,
            },
            {
                id: "pic-email",
                fieldId: "pic_email",
                label: getTranslation("col-pic-email", "PIC Email"),
                dragDisabled: true,
            },
            {
                id: "pic-phone",
                fieldId: "pic_phone",
                label: getTranslation("col-pic-phone", "PIC Phone Number"),
                dragDisabled: true,
            },
            {
                id: "status",
                fieldId: "pic_name",
                label: getTranslation("col-status", "Status"),
                render: (row) => {
                    const name = row.branch_name || "";
                    const isActive = !name.includes("1") && !name.includes("5");
                    return (
                        <div className="flex items-center gap-2">
                            <span
                                className={`h-2.5 w-2.5 rounded-full ${
                                    isActive
                                        ? "bg-primary-600 border border-primary-100"
                                        : "bg-greyScale-60 border border-greyScale-90"
                                }`}
                            />
                            <span className="text-sm font-semibold text-text-primary capitalize">
                                {isActive ? "Active" : "Inactive"}
                            </span>
                        </div>
                    );
                },
                dragDisabled: true,
            },
        ],
        [t]
    );

    const addressColumns: Columns<FlattenedAddress>[] = useMemo(
        () => [
            {
                id: "no",
                fieldId: "index",
                label: getTranslation("col-no", "No."),
                dragDisabled: true,
                width: "50px",
            },
            {
                id: "address",
                fieldId: "address_name",
                label: getTranslation("col-address", "Address"),
                render: (row) => (
                    <div className="flex flex-col gap-1 items-start">
                        <span className="font-semibold text-text-primary">{row.address_name || "—"}</span>
                        {row.is_billing_address && (
                            <span className="inline-flex items-center rounded bg-greyScale-95 px-1.5 py-0.5 text-[10px] font-semibold text-greyScale-60 border border-greyScale-90">
                                {getTranslation("billing-address-tag", "Billing Address")}
                            </span>
                        )}
                    </div>
                ),
                dragDisabled: true,
            },
            {
                id: "category",
                fieldId: "address_category",
                label: getTranslation("col-category", "Category"),
                dragDisabled: true,
                render: (row) => getOptionLabel(addressCategoryOptions, row.address_category ?? ""),
            },
            {
                id: "branch",
                fieldId: "branch_name",
                label: getTranslation("col-branch", "Branch"),
                dragDisabled: true,
            },
            {
                id: "full-address",
                fieldId: "full_address",
                label: getTranslation("col-full-address", "Full Address"),
                dragDisabled: true,
            },
            {
                id: "country",
                fieldId: "id",
                label: getTranslation("col-country", "Country"),
                render: (row) => row.country_name || row.country || "Indonesia",
                dragDisabled: true,
            },
            {
                id: "province",
                fieldId: "province_name",
                label: getTranslation("col-province", "Province"),
                render: (row) => row.province_name || row.province || row.state_name || "—",
                dragDisabled: true,
            },
            {
                id: "city",
                fieldId: "city_name",
                label: getTranslation("col-city", "City"),
                render: (row) => row.city_name || row.city || "—",
                dragDisabled: true,
            },
        ],
        [t]
    );

    if (isLoading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Typography variant="body" tone="muted">
                    {getTranslation("loading", "Loading...")}
                </Typography>
            </div>
        );
    }

    if (!detail) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Typography variant="body" tone="muted">
                    {getTranslation("data-not-found", "Data not found")}
                </Typography>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col gap-6 print-friendly-layout">
                {/* Top Action Row */}
                <div className="flex items-center justify-between">
                    <Typography variant="heading4" className="font-bold text-text-primary text-2xl">
                        {getTranslation("detail-page-title", "Vendor Information")}
                    </Typography>
                    <div className="flex items-center gap-3 no-print">
                        <Button
                            type="button"
                            variant="outline"
                            color="tertiary"
                            size="medium"
                            iconLeft={<Icon name="clock" size={16} />}
                            onClick={() => setIsHistoryOpen(true)}
                        >
                            {getTranslation("btn-history-data", "History")}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            color="tertiary"
                            size="medium"
                            iconLeft={<Icon name="download" size={16} />}
                            onClick={() => window.print()}
                        >
                            {getTranslation("btn-export", "Export")}
                        </Button>
                        <Button
                            type="button"
                            variant="fill"
                            color="primary"
                            size="medium"
                            iconLeft={<Icon name="edit-2" size={16} />}
                            onClick={() => navigate(`/master-data/vendor/${id}/edit`)}
                        >
                            {getTranslation("btn-edit", "Edit")}
                        </Button>
                    </div>
                </div>

                {/* Profile Information Card */}
                <VendorDetailCard detail={detail} />

                {/* Tabs & Table Section */}
                <section className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden print-friendly-layout">
                    {/* Tab Headers */}
                    <div className="flex items-center justify-between border-b border-border px-6 no-print">
                        <div className="flex gap-8">
                            <button
                                type="button"
                                onClick={() => setActiveTab("fleet-list")}
                                className={`relative py-4 text-sm font-semibold transition-all ${
                                    activeTab === "fleet-list"
                                        ? "text-primary-600"
                                        : "text-greyScale-60 hover:text-text-primary"
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon name="truck" size={18} />
                                    <span>{getTranslation("tab-fleet-list", "Fleet List")}</span>
                                </div>
                                {activeTab === "fleet-list" && (
                                    <span className="absolute bottom-0 left-0 h-0.5 w-full bg-primary-600" />
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab("branch-list")}
                                className={`relative py-4 text-sm font-semibold transition-all ${
                                    activeTab === "branch-list"
                                        ? "text-primary-600"
                                        : "text-greyScale-60 hover:text-text-primary"
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon name="home" size={18} />
                                    <span>{getTranslation("tab-branch-list", "Branch List")}</span>
                                </div>
                                {activeTab === "branch-list" && (
                                    <span className="absolute bottom-0 left-0 h-0.5 w-full bg-primary-600" />
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab("address-list")}
                                className={`relative py-4 text-sm font-semibold transition-all ${
                                    activeTab === "address-list"
                                        ? "text-primary-600"
                                        : "text-greyScale-60 hover:text-text-primary"
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon name="map-pin" size={18} />
                                    <span>{getTranslation("tab-address-list", "Address List")}</span>
                                </div>
                                {activeTab === "address-list" && (
                                    <span className="absolute bottom-0 left-0 h-0.5 w-full bg-primary-600" />
                                )}
                            </button>
                        </div>

                        <div className="py-2.5">
                            {activeTab === "fleet-list" ? (
                                <Button
                                    type="button"
                                    variant="fill"
                                    color="primary"
                                    size="small"
                                    iconLeft={<Icon name="plus" size={16} />}
                                    onClick={() => navigate(`/master-data/fleet/add?vendor_id=${id}`)}
                                >
                                    {getTranslation("btn-add-fleet", "Add Fleet")}
                                </Button>
                            ) : activeTab === "branch-list" ? (
                                <Button
                                    type="button"
                                    variant="fill"
                                    color="primary"
                                    size="small"
                                    iconLeft={<Icon name="plus" size={16} />}
                                    onClick={() => navigate(`/master-data/vendor/${id}/edit`)}
                                >
                                    {getTranslation("btn-add-branch", "Add Branch")}
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    variant="fill"
                                    color="primary"
                                    size="small"
                                    iconLeft={<Icon name="plus" size={16} />}
                                    onClick={() => navigate(`/master-data/vendor/${id}/edit`)}
                                >
                                    {getTranslation("btn-add-address", "Add Address")}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Tab Contents */}
                    <div className="p-6">
                        {/* Fleet List Tab */}
                        {activeTab === "fleet-list" && (
                            <div className="flex flex-col gap-4">
                                <Table<FleetRow>
                                    columns={fleetColumns}
                                    data={fleetRows}
                                    loading={isFleetLoading}
                                    rowKey="id"
                                    sortState={sortState}
                                    onSortStateChange={setSortState}
                                    enableColumnDrag={false}
                                    enableColumnResize={false}
                                />
                                
                                <div className="flex items-center justify-between border-t border-border pt-4 mt-2">
                                    <div className="flex items-center gap-2">
                                        <Typography variant="bodySmall" tone="secondary">
                                            {getTranslation("rows-per-page", "Rows per page :")}
                                        </Typography>
                                        <select
                                            value={rowsPerPage}
                                            onChange={(e) => {
                                                setRowsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="rounded-lg border border-border px-2 py-1 text-sm outline-none"
                                        >
                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                        </select>
                                    </div>
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={fleetTotalPages}
                                        onPageChange={setCurrentPage}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Branch List Tab */}
                        {activeTab === "branch-list" && (
                            <div className="flex flex-col gap-4">
                                <Table<TransportResourceVendorBranchDetail>
                                    columns={branchColumns}
                                    data={paginatedBranches}
                                    rowKey="id"
                                    enableColumnDrag={false}
                                    enableColumnResize={false}
                                />
                                
                                <div className="flex items-center justify-between border-t border-border pt-4 mt-2">
                                    <div className="flex items-center gap-2">
                                        <Typography variant="bodySmall" tone="secondary">
                                            {getTranslation("rows-per-page", "Rows per page :")}
                                        </Typography>
                                        <select
                                            value={branchRowsPerPage}
                                            onChange={(e) => {
                                                setBranchRowsPerPage(Number(e.target.value));
                                                setBranchPage(1);
                                            }}
                                            className="rounded-lg border border-border px-2 py-1 text-sm outline-none"
                                        >
                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                        </select>
                                    </div>
                                    <Pagination
                                        currentPage={branchPage}
                                        totalPages={totalBranchPages}
                                        onPageChange={setBranchPage}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Address List Tab */}
                        {activeTab === "address-list" && (
                            <div className="flex flex-col gap-4">
                                <Table<FlattenedAddress>
                                    columns={addressColumns}
                                    data={paginatedAddresses}
                                    rowKey="id"
                                    enableColumnDrag={false}
                                    enableColumnResize={false}
                                />
                                
                                <div className="flex items-center justify-between border-t border-border pt-4 mt-2">
                                    <div className="flex items-center gap-2">
                                        <Typography variant="bodySmall" tone="secondary">
                                            {getTranslation("rows-per-page", "Rows per page :")}
                                        </Typography>
                                        <select
                                            value={addressRowsPerPage}
                                            onChange={(e) => {
                                                setAddressRowsPerPage(Number(e.target.value));
                                                setAddressPage(1);
                                            }}
                                            className="rounded-lg border border-border px-2 py-1 text-sm outline-none"
                                        >
                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                        </select>
                                    </div>
                                    <Pagination
                                        currentPage={addressPage}
                                        totalPages={totalAddressPages}
                                        onPageChange={setAddressPage}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            {/* History Timeline Sidebar */}
            <VendorHistorySidebar
                open={isHistoryOpen}
                onClose={() => setIsHistoryOpen(false)}
                vendorId={id}
                detail={detail}
            />
        </>
    );
};

export default VendorDetailPage;